type Options<T> = {
  minDelayMs?: number;
  retriesLimit?: number;
  fallback?: T;
};

const DEFAULT_DELAY = 1000;
const DEFAULT_LIMIT = 5;

const asyncDelay = async (
  minDelayMs: number,
  attemptNumber: number,
): Promise<void> =>
  new Promise((resolve) => {
    if (navigator.onLine) {
      setTimeout(resolve, minDelayMs * attemptNumber);
    } else {
      const listener = () => {
        window.removeEventListener('online', listener);
        resolve();
      };
      window.addEventListener('online', listener);
    }
  });

const attempt = async <T>(
  promiseGenerator: () => Promise<T>,
  options: Options<T>,
  attemptNumber: number,
): Promise<T> => {
  const {
    retriesLimit = DEFAULT_LIMIT,
    minDelayMs = DEFAULT_DELAY,
    fallback,
  } = options;

  try {
    return await promiseGenerator();
  } catch (err) {
    if (attemptNumber < retriesLimit) {
      console.warn(
        `Promise failed on ${attemptNumber} attempt, will retry`,
        err,
      );
      return asyncDelay(minDelayMs, attemptNumber).then(() =>
        attempt(promiseGenerator, options, attemptNumber + 1),
      );
    } else {
      console.error(
        `Promise failed completely after ${attemptNumber} attempts`,
        err,
      );
      if (fallback !== undefined) {
        return fallback;
      }
      throw err;
    }
  }
};

export const retryPromise = <T>(
  promiseGenerator: () => Promise<T>,
  options?: Options<T>,
): (() => Promise<T>) => {
  return () => attempt(promiseGenerator, options || {}, 1);
};
